import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { EMPTY_ORDER_ATTR_FILTER, OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import { orderAttrFilterQuery, orderAttrFilterToken } from 'src/components/dispatch/orderAttrFilterParams';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

import { usePlanningsDeliveriesCache, usePlanningsDriversCache, usePlanningsTrucksCache } from './usePlanningsCache';

const requestInfiniteCollectionPath = (startOn: string, endOn: string, page: number, perPage: number) => `/api/v3/plannings/${startOn}/deliveries?end_on=${endOn}&page=${page}&per_page=${perPage}`;
const requestCollectionPath = (startOn: string, endOn: string) => `/api/v3/plannings/${startOn}/deliveries?end_on=${endOn}`;
const requestMemberPath = (id: number) => `/api/v3/plannings/deliveries/${id}`;

const getInfiniteEntities = (startOn: string, endOn: string, perPage: number, queryClient: QueryClient) => async ({ pageParam = 1, queryKey }) => {
  const requestPath = requestInfiniteCollectionPath(startOn, endOn, pageParam, perPage);
  const response: AxiosResponse<{
    ids: number[],
    total_pages_count: number;
    data: PlanningsDeliveryEntity[];
  }> = await axios.get(requestPath);
  return response.data;
};

const getEntities = (startOn: string, endOn: string, queryClient: QueryClient) => async () => {
  const response: AxiosResponse<PlanningsDeliveryEntity[]> = await axios.get(requestCollectionPath(startOn, endOn));

  return response.data.map((entity) => {
    queryClient.setQueryData(['useQueryPlanningsDelivery', { id: entity.id }], entity);

    return entity;
  });
};

const getEntity = (id: number) => async () => {
  const response: AxiosResponse<PlanningsDeliveryEntity> = await axios.get(requestMemberPath(id));

  return response.data;
};

export const USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY = 'useQueryPlanningsDeliveries';
export const useInfiniteQueryPlanningsDeliveries = (startOn: string, endOn: string, perPage: number) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: [USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY, startOn, endOn, perPage],
    queryFn: getInfiniteEntities(startOn, endOn, perPage, queryClient),
    initialPageParam: 1,
    staleTime: Infinity,
    enabled: Boolean(startOn) && Boolean(endOn),
    getNextPageParam: (lastPage, allPages) => (allPages.length < lastPage.total_pages_count ? allPages.length + 1 : undefined),
  });
};

export const buildDeliveriesRequestPath = (
  startOn: string,
  endOn: string,
  page: number,
  perPage: number,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
) => `/api/v5/plannings/${startOn}/deliveries?end_on=${endOn}&page=${page}&per_page=${perPage}`
  + orderAttrFilterQuery(orderAttrFilter);

export const PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY = 'useInfiniteQueryPlanningsDeliveriesWithLegRuns';

/**
 * 表示対象は**末尾**に足す。PlanningsDeliveriesWithLegRunsCache が
 * `queryCacheKey[1]` / `[2]` を startOn / endOn として位置で読むため、途中には挿せない
 */
export const buildDeliveriesQueryKey = (
  startOn: string,
  endOn: string,
  perPage: number,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
) => [
  PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY, startOn, endOn, perPage, orderAttrFilterToken(orderAttrFilter),
] as const;

export type DeliveriesPage = {
  ids: number[];
  total_pages_count: number;
  data: PlanningsDeliveryWithLegRunEntity[];
};

/**
 * delivery 一覧の全ページを取り切る。page 1 で総ページ数を見て、残りは並列で取る。
 *
 * Why not useInfiniteQuery を呼び出し側の useEffect で 1 ページずつ進める（以前の形）:
 * 62 台規模では 6 ページを**直列**に取っており、dev 実測で 22.5 秒 / 3.7MB かかっていた。
 * 加えてページが届くたびに deliveries の参照が変わるので、行 VM の作り直しと 3 カラムの
 * 再描画がページ数だけ繰り返されていた（初期表示の React commit 45 回の主因。
 * log_web_issues#301 の計測）。1 度で取り切れば更新は 1 回で済む。
 */
export const fetchAllDeliveryPages = async (
  startOn: string,
  endOn: string,
  perPage: number,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
): Promise<DeliveriesPage[]> => {
  const getPage = (page: number): Promise<AxiosResponse<DeliveriesPage>> =>
    axios.get(buildDeliveriesRequestPath(startOn, endOn, page, perPage, orderAttrFilter));

  const first = await getPage(1);
  const totalPages = first.data.total_pages_count;
  if (totalPages <= 1) return [first.data];

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) => index + 2).map((page) => getPage(page)),
  );
  return [first.data, ...rest.map((response) => response.data)];
};

/** 全ページを 1 度に取るので「次のページ」は無い。呼び出し側の infinite 互換のために形だけ返す。 */
const NO_NEXT_PAGE = {
  hasNextPage: false,
  isFetchingNextPage: false,
  fetchNextPage: () => Promise.resolve(undefined),
} as const;

/**
 * delivery 一覧の唯一の取得口。/map・カード配車・V3Plans・V3Timeline が共有する。
 *
 * 以前はカード配車系だけ別フック（useConvenienceDispatchDeliveriesQuery）を持っており、
 * 同じ API を叩きながらキャッシュキーが分かれていた。片方の画面で配車しても
 * もう片方の invalidate が届かないため、1 つに寄せている。
 *
 * 中身は useQuery で全ページを取り切る（fetchAllDeliveryPages）。**queryFn が InfiniteData
 * （`{ pages, pageParams }`）を返すので、QueryClient に入る形は useInfiniteQuery のときと同じ。**
 * 呼び出し側の `data.pages.flatMap(...)` だけでなく、キャッシュを直接触る既存処理
 * （setQueryData で `old.pages.map(...)` を呼ぶもの）もそのまま動く。
 * `hasNextPage` は常に false なので、呼び出し側に残っていた「全ページを自動で進める
 * useEffect」は不要（削除済み）。
 */
export const useInfiniteQueryPlanningsDeliveriesWithLegRuns = (
  startOn: string,
  endOn: string,
  perPage: number,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
  // カード配車の Container が取得の開始を遅らせるときに使う
  enabled = true,
) => {
  const query = useQuery({
    // queryFn の戻り値がそのまま QueryClient に入る。**ここで InfiniteData の形にしておく**
    // （フックの出口で包み直すと、キャッシュの中身は配列のままになる）。
    // 配車リセット・車両色の楽観更新・V3Timeline の仮車両割当・PlanningsDeliveriesWithLegRunsCache は
    // setQueryData で `old.pages.map(...)` を直に呼ぶので、形が崩れると例外で落ちる
    queryKey: buildDeliveriesQueryKey(startOn, endOn, perPage, orderAttrFilter),
    queryFn: async () => {
      const pages = await fetchAllDeliveryPages(startOn, endOn, perPage, orderAttrFilter);
      return { pages, pageParams: pages.map((_, index) => index + 1) };
    },
    staleTime: Infinity,
    enabled: enabled && Boolean(startOn) && Boolean(endOn),
    // 絞り込みを切り替えた瞬間に中央ボードが空になるのを防ぐ。
    // Why not keepPreviousData をそのまま使う: このクエリは日付変更でもキーが変わるため、
    // 素通しすると前日のデータを表示したまま isLoading が false になり、
    // Map/Presenter.tsx の resolveAutoDispatchAvailability を経由して自動配車ボタンが
    // 押せてしまう（issue #317 レビュー I-1）。
    //
    // 前のデータを使ってよいのは「絞り込みトークンだけが変わった」とき。トークンはキーの
    // 末尾なので、それ以外が全部一致するかで判定する。
    // Why not 日付だけ比べる: perPage が変わっても前のデータを返してしまい、
    // 別の一覧を表示したまま isLoading が false になる（Codex レビュー P1-b）
    placeholderData: (previous, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      if (!previousKey) return undefined;
      const currentKey = buildDeliveriesQueryKey(startOn, endOn, perPage, orderAttrFilter);
      const sameExceptFilter = currentKey
        .slice(0, -1)
        .every((part, index) => part === previousKey[index]);
      return sameExceptFilter ? previous : undefined;
    },
  });

  return useMemo(() => ({ ...query, ...NO_NEXT_PAGE }), [query]);
};

/** 上のフックの戻り値。呼び出し側の props 型に使う（infinite 互換だが useQuery ベース）。 */
export type PlanningsDeliveriesQueryResult = ReturnType<typeof useInfiniteQueryPlanningsDeliveriesWithLegRuns>;

export const useQueryPlanningsDeliveries = (startOn: string, endOn: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY, { startOn, endOn }],
    queryFn: getEntities(startOn, endOn, queryClient),
    staleTime: Infinity,
    enabled: Boolean(startOn) && Boolean(endOn)
  });
};

export const useQueryPlanningsDelivery = (id: number) => useQuery({
  queryKey: ['useQueryPlanningsDelivery', { id }],
  queryFn: getEntity(id),
  staleTime: Infinity,
  enabled: Boolean(id)
});

export const useMutationPlanningDelivery = () => {
  const deliveriesCache = usePlanningsDeliveriesCache();
  const trucksCache = usePlanningsTrucksCache();
  const driversCache = usePlanningsDriversCache();

  return {
    delete: useMutation({
      mutationFn: (deliveryToDelete: PlanningsDeliveryEntity) => axios.delete(requestMemberPath(deliveryToDelete.id)),

      onSuccess: (_data, variables) => {
        deliveriesCache.removeDeliveryById(variables.id);
        trucksCache.removeTruckById(variables.truckId);
        driversCache.removeDriverById(variables.driverId);
      }
    }),
  };
};
